import { createClient } from '@libsql/client';
import { nanoid } from 'nanoid';

const db = createClient({
  url: import.meta.env.VITE_TURSO_DB_URL,
  authToken: import.meta.env.VITE_TURSO_AUTH_TOKEN
});

export const aboutService = {
  async getAboutContent() {
    try {
      await this.setupAboutTable(); 

      const result = await db.execute("SELECT *, updated_at FROM about_content WHERE id = 'about_page_content'");
      if (result.rows.length === 0) {
        console.warn("About content not found after setup. Re-initializing.");
        await this.initializeDefaultAboutContent();
        const defaultResult = await db.execute("SELECT *, updated_at FROM about_content WHERE id = 'about_page_content'");
        return this.parseAboutContent(defaultResult.rows[0]);
      }
      return this.parseAboutContent(result.rows[0]);
    } catch (error) {
      console.error('Error fetching about content:', error);
      throw error;
    }
  },

  async initializeDefaultAboutContent() {
    const check = await db.execute("SELECT id FROM about_content WHERE id = 'about_page_content'");
    if (check.rows.length === 0) {
      const now = new Date().toISOString(); 
      const defaultContent = {
        hero: {
          heading: "Crafting Spaces, Building Dreams",
          subheading: "At Antacres, we believe in transforming visions into reality. Discover our journey.",
          image: "https://i.ibb.co/dL3R2Wq/about-hero.jpg"
        },
        mission_vision: {
          mission_heading: "Our Mission",
          mission_text_1: "To deliver unparalleled real estate solutions that exceed client expectations, foster sustainable communities, and create lasting value.",
          mission_text_2: "We are committed to integrity, innovation, and excellence in every project we undertake.",
          vision_heading: "Our Vision",
          vision_text: "To be the leading real estate developer recognized for our architectural brilliance, commitment to quality, and positive societal impact.",
          image: "https://i.ibb.co/T4X510s/mission-vision.jpg"
        },
        principles: [
          { title: "Integrity", description: "Operating with honesty and transparency in all our dealings.", icon: "Handshake" },
          { title: "Innovation", description: "Constantly seeking new and better ways to build and serve.", icon: "Lightbulb" },
          { title: "Excellence", description: "Committing to the highest standards in every project.", icon: "Award" },
          { title: "Customer Focus", description: "Prioritizing client needs and delivering tailored solutions.", icon: "Users" },
          { title: "Sustainability", description: "Developing eco-friendly and energy-efficient properties.", icon: "TrendingUp" },
          { title: "Community", description: "Building spaces that enrich lives and foster vibrant communities.", icon: "Home" },
        ],
        glance_stats: [
          { number: "15+", label: "Years in Business" },
          { number: "200+", label: "Properties Sold" },
          { number: "50+", label: "Awards Won" },
          { number: "100%", label: "Client Satisfaction" },
        ],
        team: [
          { name: "Lakshmi Narayanan", position: "Founder & CEO", image: "https://i.ibb.co/S6w77Lg/team1.jpg", bio: "Visionary leader with 20+ years in real estate development." },
          { name: "Ramesh Kumar", position: "Chief Architect", image: "https://i.ibb.co/NCV0jQ5/team2.jpg", bio: "Award-winning architect, specializing in sustainable designs." },
          { name: "Priya Sharma", position: "Head of Sales", image: "https://i.ibb.co/QN02qTz/team3.jpg", bio: "Dynamic sales professional with a proven track record." }
        ],
        cta: {
          heading: "Ready to find your perfect property?",
          subheading: "Contact Antacres today and let our expert team guide you every step of the way.",
          button_text: "Contact Us",
          button_link: "/contact"
        }
      };

      await db.execute({
        sql: `INSERT INTO about_content (id, hero_content, mission_vision_content, principles_content, glance_stats_content, team_content, cta_content, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
        args: [
          'about_page_content',
          JSON.stringify(defaultContent.hero),
          JSON.stringify(defaultContent.mission_vision),
          JSON.stringify(defaultContent.principles),
          JSON.stringify(defaultContent.glance_stats),
          JSON.stringify(defaultContent.team),
          JSON.stringify(defaultContent.cta),
          now 
        ]
      });
      console.log('Default about content initialized.');
    }
  },

  parseAboutContent(row) {
    if (!row) return null;
    return {
      hero: JSON.parse(row.hero_content),
      mission_vision: JSON.parse(row.mission_vision_content),
      principles: JSON.parse(row.principles_content),
      glance_stats: JSON.parse(row.glance_stats_content),
      team: JSON.parse(row.team_content),
      cta: JSON.parse(row.cta_content),
      updated_at: row.updated_at 
    };
  },

  async updateAboutSection(sectionName, content) {
    const now = new Date().toISOString(); 
    try {
      const columnName = `${sectionName}_content`;
      await db.execute({
        sql: `UPDATE about_content SET ${columnName} = ?, updated_at = ? WHERE id = 'about_page_content'`,
        args: [JSON.stringify(content), now] 
      });
      console.log(`About section '${sectionName}' updated successfully.`);
      return true;
    } catch (error) {
      console.error(`Error updating about section '${sectionName}':`, error);
      throw error;
    }
  },

  async setupAboutTable() {
    try {
      await db.execute(`
        CREATE TABLE IF NOT EXISTS about_content (
          id TEXT PRIMARY KEY,
          hero_content TEXT,
          mission_vision_content TEXT,
          principles_content TEXT,
          glance_stats_content TEXT,
          team_content TEXT,
          cta_content TEXT,
          updated_at TEXT -- Add updated_at column
        );
      `);
      console.log('About content table checked/created.');
      await this.initializeDefaultAboutContent();
    } catch (error) {
      console.error('Error setting up about content table:', error);
      throw error;
    }
  }
};
